import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Loader } from "lucide-react";

import { completeConnect } from "../../api/googleAnalytics";
import { errorMessage } from "./errors";

/**
 * Landing page for Google's OAuth redirect.
 *
 * Posts the code and state to the backend, then hands off to the dashboard with `replace: true` so
 * the browser Back button cannot re-submit a spent code.
 */
const GoogleAnalyticsCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [exchangeError, setExchangeError] = useState(null);
  // React 18 StrictMode mounts effects twice in development; the code is single-use,
  // so a second exchange would always fail and overwrite a successful result.
  const exchanged = useRef(false);

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const denied = searchParams.get("error");

  // Whether the redirect is usable is knowable from the URL alone, so it is derived
  // during render rather than pushed into state from inside the effect.
  let paramError = null;
  if (denied) {
    paramError = "Google sign-in was cancelled.";
  } else if (!code || !state) {
    paramError = "Google did not return a valid response. Please try connecting again.";
  }

  useEffect(() => {
    if (paramError || exchanged.current) return;
    exchanged.current = true;

    completeConnect(code, state)
      .then(() => navigate("/google-analytics", { replace: true }))
      .catch((err) =>
        setExchangeError(
          errorMessage(
            err,
            "We could not finish connecting your Google account. Please try again.",
          ),
        ),
      );
  }, [code, state, paramError, navigate]);

  const error = paramError || exchangeError;

  return (
    <div className="min-h-screen pt-10 pb-16 px-4 md:px-8">
      <div className="max-w-2xl mx-auto">
        {error ? (
          <div className="bg-surface-container-low rounded-2xl p-8 border border-surface-container-high">
            <h1 className="font-headline text-xl font-bold text-on-surface">
              Could not connect Google Analytics
            </h1>
            <p className="text-sm text-on-surface-variant mt-2">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/google-analytics", { replace: true })}
              className="mt-6 px-5 py-2.5 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
            >
              Back to Google Analytics
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader className="w-6 h-6 animate-spin text-primary" />
            <span className="text-on-surface-variant text-sm">Finishing the connection…</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleAnalyticsCallback;
